import { Platform } from 'react-native';

export interface NativeUploadImage {
  uri: string;
  name: string;
  type: string;
  width?: number;
  height?: number;
}

export type UploadImage = File | NativeUploadImage;

const TARGET_SIZE_RATIO = 0.5;
const TARGET_DIMENSION_SCALE = Math.sqrt(TARGET_SIZE_RATIO);

const isWeb = Platform.OS === 'web' && typeof document !== 'undefined';

const createCompressedName = (name: string, extension: string): string => {
  const dotIndex = name.lastIndexOf('.');
  const base = dotIndex > 0 ? name.slice(0, dotIndex) : name;
  return `${base}_compressed.${extension}`;
};

const loadWebImage = (file: File): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const img = new Image();

    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(img);
    };

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('Unable to load image for compression.'));
    };

    img.src = objectUrl;
  });

const canvasToBlob = (canvas: HTMLCanvasElement, mimeType: string, quality?: number): Promise<Blob> =>
  new Promise((resolve, reject) => {
    canvas.toBlob(
      blob => {
        if (!blob) {
          reject(new Error('Unable to compress image.'));
          return;
        }
        resolve(blob);
      },
      mimeType,
      quality,
    );
  });

const compressWebFile = async (file: File): Promise<File> => {
  const img = await loadWebImage(file);
  const targetWidth = Math.max(1, Math.round(img.naturalWidth * TARGET_DIMENSION_SCALE));
  const targetHeight = Math.max(1, Math.round(img.naturalHeight * TARGET_DIMENSION_SCALE));

  const canvas = document.createElement('canvas');
  canvas.width = targetWidth;
  canvas.height = targetHeight;

  const ctx = canvas.getContext('2d');
  if (!ctx) {
    return file;
  }

  ctx.drawImage(img, 0, 0, targetWidth, targetHeight);

  const originalType = file.type.toLowerCase();
  const useJpeg = originalType === 'image/jpeg' || originalType === 'image/jpg';
  const mimeType = useJpeg ? 'image/jpeg' : 'image/png';
  const quality = useJpeg ? 0.75 : undefined;
  const compressedBlob = await canvasToBlob(canvas, mimeType, quality);

  if (compressedBlob.size >= file.size) {
    return file;
  }

  const extension = mimeType === 'image/png' ? 'png' : 'jpg';
  return new File([compressedBlob], createCompressedName(file.name, extension), {
    type: mimeType,
    lastModified: Date.now(),
  });
};

const compressNativeFile = async (file: NativeUploadImage): Promise<NativeUploadImage> => {
  const width = file.width ?? 1600;
  const height = file.height ?? 1200;

  const targetWidth = Math.max(1, Math.round(width * TARGET_DIMENSION_SCALE));
  const targetHeight = Math.max(1, Math.round(height * TARGET_DIMENSION_SCALE));
  const isPng = file.type.toLowerCase().includes('png');
  const format = isPng ? 'PNG' : 'JPEG';

  const resizerModule = require('@bam.tech/react-native-image-resizer');
  const createResizedImage = resizerModule?.default?.createResizedImage ?? resizerModule?.createResizedImage;

  if (typeof createResizedImage !== 'function') {
    return file;
  }

  const resized = await createResizedImage(
    file.uri,
    targetWidth,
    targetHeight,
    format,
    50,
    0,
  );

  const mimeType = format === 'PNG' ? 'image/png' : 'image/jpeg';

  return {
    uri: resized.uri,
    name: resized.name || createCompressedName(file.name, format === 'PNG' ? 'png' : 'jpg'),
    type: mimeType,
    width: resized.width,
    height: resized.height,
  };
};

export const compressImageForUpload = async (file: UploadImage): Promise<UploadImage> => {
  try {
    if (isWeb && typeof File !== 'undefined' && file instanceof File) {
      return await compressWebFile(file);
    }

    if (!isWeb && typeof file === 'object' && file !== null && 'uri' in file) {
      return await compressNativeFile(file as NativeUploadImage);
    }

    return file;
  } catch {
    return file;
  }
};
